// =============================================================================
// Supplier reference - display precedence and search fields
// =============================================================================
function trimmedOrNull(value) {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
}
/**
 * Resolve the single reference to display for an expense, in precedence order:
 * the document number, then our own reference, then the supplier's.
 *
 * Returns null only when the document carried none of them, which is the case a
 * caller should render as "no reference" - distinct from the pre-split
 * behaviour, where an expense carrying only a customer reference read as having
 * no reference at all because the document number was the only field consulted.
 *
 * Our reference outranks the supplier's because it is the value someone in the
 * firm chose and will recognise; the supplier's internal code is meaningful to
 * them, not to us. The account number is deliberately NOT in this chain: it
 * identifies the trading relationship rather than the document, so showing it in
 * a per-document reference slot would label every expense from one supplier
 * identically. It is still searchable - see `expenseReferenceSearchValues`.
 */
export function resolveExpenseDisplayReference(expense) {
    return (trimmedOrNull(expense.supplierInvoiceNumber) ??
        trimmedOrNull(expense.customerReference) ??
        trimmedOrNull(expense.supplierOwnReference));
}
/**
 * Every reference value a free-text search over expenses should match against,
 * with blanks dropped.
 *
 * Wider than `resolveExpenseDisplayReference` on purpose: a reconciler typing
 * whatever appears in a bank narration should find the expense whether that
 * string is the invoice number, either reference, or the account code the
 * supplier uses as the payment reference. Restricting search to the displayed
 * value would make an expense unfindable by a reference it genuinely carries.
 */
export function expenseReferenceSearchValues(expense) {
    return [
        expense.supplierInvoiceNumber,
        expense.customerReference,
        expense.supplierOwnReference,
        expense.customerAccountNumber,
    ]
        .map(trimmedOrNull)
        .filter((value) => value !== null);
}
/**
 * Case-insensitive substring match of `term` against any of an expense's
 * reference values. `term` is expected pre-trimmed and lowercased by the caller
 * (search loops do it once for the whole pool, not once per row).
 */
export function matchesExpenseReference(expense, lowercasedTerm) {
    return expenseReferenceSearchValues(expense).some((value) => value.toLowerCase().includes(lowercasedTerm));
}
